<script setup lang="ts">
import { useDebounceFn } from "@vueuse/core";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";
import { computed, ref } from "vue";
import {
  hasDisplayCustomPricingWriteActionsEnabled,
  hasProgramsWritePermission,
  isTenantManager,
} from "@/composables/authorization";
import BulkInsertModal from "./BulkInsertModal/index.vue";
import CreateEditModal from "./CreateEditModal/index.vue";
import CustomPricingFilters from "./CustomPricingFilters/index.vue";
import CustomPricingTable from "./CustomPricingTable/index.vue";
import { useFilters } from "./composables/useFilters";
import { customPricingPresets } from "./styles";

defineOptions({ name: "CustomPricing" });

const { q, updateQ } = useFilters();

const createEditVisible = ref<boolean>(false);
const bulkInsertVisible = ref<boolean>(false);
const editingOverrideId = ref<string | undefined>(undefined);

const debouncedUpdateQ = useDebounceFn(
  (value?: string) => {
    updateQ(value);
  },
  1000,
  { maxWait: 5000 },
);

const openCreate = (): void => {
  editingOverrideId.value = undefined;
  createEditVisible.value = true;
};

const openEdit = (overrideId: string): void => {
  editingOverrideId.value = overrideId;
  createEditVisible.value = true;
};

const openBulkInsert = (): void => {
  bulkInsertVisible.value = true;
};

const canShowBulkImport = computed<boolean>(
  () => Boolean(hasDisplayCustomPricingWriteActionsEnabled.value) && isTenantManager(),
);

const canShowCreateOverride = computed<boolean>(
  () =>
    Boolean(hasDisplayCustomPricingWriteActionsEnabled.value) && (hasProgramsWritePermission() || isTenantManager()),
);
</script>

<template>
  <div class="mt-6 mx-6">
    <div class="flex font-normal font-montserrat w-full mt-6 justify-end gap-2">
      <IconField iconPosition="left" class="w-2/4 pr-2">
        <InputIcon class="input-icon-position">
          <span class="material-symbols-outlined text-green-800 text-center text-3xl ml-4">search</span>
        </InputIcon>
        <InputText
          :pt="customPricingPresets.inputtext"
          :model-value="q"
          placeholder="SEARCH"
          @update:modelValue="(value) => debouncedUpdateQ(value)"
          class="ml-4 w-full block font-montserrat font-bold text-base text-university-primary leading-5 py-1 pr-1 border border-gray-475 rounded-full h-10 placeholder-university-primary bg-white"
        />
      </IconField>
      <button
        v-if="canShowBulkImport"
        class="flex items-center gap-1 rounded-full border border-teal-900 px-4 h-10 text-sm font-semibold text-teal-900 bg-white hover:bg-teal-50 ml-4"
        @click="openBulkInsert"
      >
        <span class="material-symbols-outlined text-lg">upload_file</span>
        Bulk Import
      </button>
      <button
        v-if="canShowCreateOverride"
        class="flex items-center gap-1 rounded-full bg-teal-900 px-4 h-10 text-sm font-semibold text-white hover:bg-teal-800"
        @click="openCreate"
      >
        <span class="material-symbols-outlined text-lg">add</span>
        Create Override
      </button>
    </div>

    <CustomPricingFilters />
    <CustomPricingTable @edit="openEdit" />

    <CreateEditModal
      v-model:visible="createEditVisible"
      :override-id="editingOverrideId"
    />
    <BulkInsertModal v-model:visible="bulkInsertVisible" />
  </div>
</template>

<style scoped>
.input-icon-position {
  top: 12px;
}
</style>
